"use client";

import { useState } from "react";
import { Detail, SkillCard } from "./characterViews";

const LOADOUT_MAX_DEFAULT = 6;

type Sub = "all" | "battle" | "mastery";

/**
 * スキルのタブ。中を 一覧・戦闘用・体得 のタブに分ける。
 * 一覧は持つスキルの全て。戦闘用は選んだスキルと、戦闘パッシブスキル（戦闘に必ず持っていくパッシブ）と、畳んだ選べるアクティブ。選べるのは battle.json を持つアクティブで、上限は loadout_max。
 * 体得は体得ごとの中身。onSave を渡さないときは見るだけで、選んだスキルを替えられない
 */
export default function PlaySkills({ detail, onSave }: { detail: Detail; onSave?: (loadout: string[]) => Promise<string | null> }) {
  const [sub, setSub] = useState<Sub>("all");
  const max = detail.loadout_max ?? LOADOUT_MAX_DEFAULT;
  const initial = detail.skills.filter((s) => s.loadout && !s.passive).map((s) => s.slug);
  const [picked, setPicked] = useState<string[]>(initial);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");
  const changed = picked.length !== initial.length || picked.some((s) => !initial.includes(s));
  const passives = detail.skills.filter((s) => s.passive && s.battle);
  const actives = detail.skills.filter((s) => !s.passive && s.battle);
  const mastery = detail.mastery ?? [];

  const toggle = (slug: string) => {
    setNote("");
    setPicked((now) => (now.includes(slug) ? now.filter((s) => s !== slug) : now.length >= max ? now : [...now, slug]));
  };
  const save = async () => {
    if (!onSave) return;
    setSaving(true);
    const error = await onSave(picked);
    setSaving(false);
    setNote(error ?? "戦闘に持っていくスキルを決めました");
  };

  return (
    <div>
      <div className="pl-subtabs pl-subtabs-3" role="tablist">
        {([["all", "一覧"], ["battle", "戦闘用"], ["mastery", "体得"]] as const).map(([key, label]) => (
          <button key={key} type="button" role="tab" aria-selected={sub === key} className={sub === key ? "pl-subtab pl-subtab-on" : "pl-subtab"} onClick={() => setSub(key)}>
            {label}
          </button>
        ))}
      </div>

      {sub === "all" ? (
        <section className="pl-prof-sec">
          {detail.skills.length === 0 ? <p className="pl-prof-p">スキルを持っていません。</p> : null}
          {detail.skills.map((skill) => (
            <SkillCard key={skill.slug} skill={{ ...skill, loadout: picked.includes(skill.slug) || (skill.passive && Boolean(skill.battle)) }} />
          ))}
        </section>
      ) : null}

      {sub === "battle" ? (
        <>
          <section className="pl-prof-sec">
            <div className="pl-sk-head">
              <h3>
                選んだスキル <small>{picked.length} / {max}</small>
              </h3>
              {onSave ? (
                <button type="button" className="pl-tab pl-tab-on" disabled={!changed || saving} onClick={save}>
                  {saving ? "決めています" : "決める"}
                </button>
              ) : null}
            </div>
            {note ? <p className="pl-sk-note">{note}</p> : null}
            <div className="pl-sk-slots">
              {Array.from({ length: max }, (_, index) => {
                const slug = picked[index];
                const skill = slug ? detail.skills.find((s) => s.slug === slug) : undefined;
                return skill ? (
                  onSave ? (
                    <button key={index} type="button" className="pl-sk-slot pl-sk-slot-on" onClick={() => toggle(skill.slug)} aria-label={`${skill.name}を外す`}>
                      <span>
                        {skill.name}
                        {(skill.missing_equipment ?? []).length > 0 ? <small className="review-warn">（使えない）</small> : null}
                      </span>
                      <i>✕</i>
                    </button>
                  ) : (
                    <span key={index} className="pl-sk-slot pl-sk-slot-on">
                      <span>
                        {skill.name}
                        {(skill.missing_equipment ?? []).length > 0 ? <small className="review-warn">（使えない）</small> : null}
                      </span>
                    </span>
                  )
                ) : (
                  <span key={index} className="pl-sk-slot">空き</span>
                );
              })}
            </div>
          </section>
          <section className="pl-prof-sec">
            <h3>戦闘パッシブスキル</h3>
            {passives.length === 0 ? <p className="pl-prof-p">ありません。</p> : passives.map((skill) => <SkillCard key={skill.slug} skill={{ ...skill, loadout: false }} />)}
          </section>
          {onSave ? (
          <details className="pl-prof-sec pl-fold">
            <summary>選べるスキル</summary>
            <div className="pl-fold-body">
            {actives.length === 0 ? <p className="pl-prof-p">ありません。</p> : null}
            {actives.map((skill) => {
              const on = picked.includes(skill.slug);
              return (
                <div key={skill.slug} className="pl-sk-row">
                  <div className="pl-sk-card">
                    <SkillCard skill={{ ...skill, loadout: false }} />
                  </div>
                  <button type="button" className={on ? "pl-sk-pick pl-sk-pick-on" : "pl-sk-pick"} aria-pressed={on} disabled={!on && picked.length >= max} onClick={() => toggle(skill.slug)}>
                    {on ? "✓ 選択中" : "選ぶ"}
                  </button>
                </div>
              );
            })}
            </div>
          </details>
          ) : null}
        </>
      ) : null}

      {sub === "mastery" ? (
        <section className="pl-prof-sec">
          {mastery.length === 0 ? <p className="pl-prof-p">体得はありません。</p> : null}
          {mastery.map((m) => (
            <details className="review-card" key={m.slug}>
              <summary>
                <span className="review-card-name">{m.name}</span>
                <span className="review-card-meta">
                  {[m.difficulty !== null ? `難易度 ${m.difficulty}` : "", m.way ?? "", `スキル ${m.skills} 本`].filter(Boolean).join("・")}
                </span>
              </summary>
              {m.summary ? <p className="review-p">{m.summary}</p> : null}
              {(m.skill_slugs ?? []).length > 0 ? (
                <div className="pl-sk-mastery">
                  <h4 className="review-h4">覚えられるスキル</h4>
                  {(m.skill_slugs ?? []).map((slug, index) => {
                    const skill = detail.skills.find((s) => s.slug === slug);
                    return skill ? (
                      <SkillCard key={slug} skill={skill} />
                    ) : (
                      <p className="review-p" key={slug}>
                        {(m.skill_names ?? [])[index] ?? slug}
                      </p>
                    );
                  })}
                </div>
              ) : null}
            </details>
          ))}
        </section>
      ) : null}
    </div>
  );
}
